import React, { useState, useEffect } from 'react';
import Consent from './components/Consent';
import Instructions from './components/Instructions';
import Demographics from './components/Demographics';
import ArtistBlock from './components/ArtistBlock';
import PostTaskCheck from './components/PostTaskCheck';
import DataExport from './components/DataExport';
import ExperimentSimulator from './components/ExperimentSimulator';
import { artists } from './data/artists';
import { shuffleArray } from './utils/randomization';

function App() {
  const [phase, setPhase] = useState('consent');
  const [phaseHistory, setPhaseHistory] = useState(['consent']);
  const [participantData, setParticipantData] = useState({
    participantID: `P${Date.now()}`,
    demographics: {},
    blocks: [],
    postTaskChecks: {
      recognition: [],
      sourceAttribution: []
    }
  });
  const [artistOrder, setArtistOrder] = useState([]);
  const [currentArtistIndex, setCurrentArtistIndex] = useState(0);
  const [showSimulator, setShowSimulator] = useState(false);
  const [navigationMode, setNavigationMode] = useState(false);
  
  // Track current position for navigation
  const [currentBlockPhase, setCurrentBlockPhase] = useState('intro');
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  const [currentPostTaskIndex, setCurrentPostTaskIndex] = useState(0);
  
  // Reviewer navigation state - just for jumping to sections
  const [reviewerNav, setReviewerNav] = useState({
    blockPhase: null,
    trialIndex: null,
    postTaskIndex: null
  });

  useEffect(() => {
    const shuffledArtists = shuffleArray([...artists]);
    setArtistOrder(shuffledArtists);
  }, []);

  const changePhase = (newPhase) => {
    setPhase(newPhase);
    setPhaseHistory(prev => [...prev, newPhase]);
  };

  const handleConsentComplete = () => {
    changePhase('instructions');
  };

  const handleInstructionsComplete = () => {
    changePhase('demographics');
  };

  const handleDemographicsComplete = (data) => {
    setParticipantData(prev => ({
      ...prev,
      demographics: data
    }));
    changePhase('experiment');
    setCurrentBlockPhase('intro');  // Initialize block phase
    setCurrentTrialIndex(0);
  };

  const handleBlockComplete = (blockData) => {
    setParticipantData(prev => ({
      ...prev,
      blocks: [...prev.blocks, blockData]
    }));

    if (currentArtistIndex < artistOrder.length - 1) {
      setCurrentArtistIndex(currentArtistIndex + 1);
      setCurrentBlockPhase('intro');  // Reset to intro for next block
      setCurrentTrialIndex(0);
    } else {
      changePhase('postTask');
      setCurrentPostTaskIndex(0);
    }
  };

  const handlePostTaskComplete = (checkData) => {
    setParticipantData(prev => ({
      ...prev,
      postTaskChecks: checkData
    }));
    changePhase('export');
  };


  // Helper function to get current dropdown value
  const getCurrentDropdownValue = () => {
    if (phase === 'experiment') {
      const blockNum = currentArtistIndex + 1;
      // Always return just the block intro value since we only show blocks in dropdown
      return `block_${blockNum}_intro`;
    } else if (phase === 'postTask') {
      const imageNum = (reviewerNav.postTaskIndex || 0) + 1;
      const checkType = imageNum <= 5 ? 'recognition' : 'attribution';
      return `postTask_${imageNum}_${checkType}`;
    } else {
      return phase;
    }
  };

  // Automatically exit navigation mode after positioning
  useEffect(() => {
    if (navigationMode) {
      // Auto-exit navigation mode after a brief delay to allow state sync
      const timer = setTimeout(() => {
        setNavigationMode(false);
        setReviewerNav({
          blockPhase: null,
          trialIndex: null,
          postTaskIndex: null
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [navigationMode]);

  // Reviewer navigation - just jumps to sections without affecting state
  const navigateToSection = (section) => {
    const [mainPhase, ...subParts] = section.split('_');
    
    // Enable navigation mode
    setNavigationMode(true);
    
    if (mainPhase === 'block') {
      const blockIndex = parseInt(subParts[0]) - 1;
      // Always navigate to intro when using dropdown (since dropdown only has block-level options)
      
      setCurrentArtistIndex(blockIndex);
      setPhase('experiment');
      setCurrentBlockPhase('intro');  // Always start at intro
      setCurrentTrialIndex(0);  // Reset trial index
      setReviewerNav({
        blockPhase: 'intro',
        trialIndex: null,
        postTaskIndex: null
      });
    } else if (mainPhase === 'postTask') {
      const imageIndex = subParts[0] ? parseInt(subParts[0]) - 1 : 0;
      
      setPhase('postTask');
      setCurrentPostTaskIndex(imageIndex);
      setReviewerNav({
        blockPhase: null,
        trialIndex: null,
        postTaskIndex: imageIndex
      });
    } else {
      setPhase(mainPhase);
      setReviewerNav({
        blockPhase: null,
        trialIndex: null,
        postTaskIndex: null
      });
    }
  };

  const navigatePrev = () => {
    // Enable navigation mode so child components receive reviewerNav updates
    setNavigationMode(true);
    
    if (phase === 'experiment') {
      // Navigate through experiment sections
      if (currentBlockPhase === 'trials' && currentTrialIndex > 0) {
        // Go to previous trial
        const newIndex = currentTrialIndex - 1;
        setCurrentTrialIndex(newIndex);
        setReviewerNav({ blockPhase: 'trials', trialIndex: newIndex, postTaskIndex: null });
      } else if (currentBlockPhase === 'trials') {
        // Go from trials to familiarization
        setCurrentBlockPhase('familiarization');
        setReviewerNav({ blockPhase: 'familiarization', trialIndex: null, postTaskIndex: null });
      } else if (currentBlockPhase === 'familiarization') {
        // Go from familiarization to intro
        setCurrentBlockPhase('intro');
        setReviewerNav({ blockPhase: 'intro', trialIndex: null, postTaskIndex: null });
      } else if (currentBlockPhase === 'intro' && currentArtistIndex > 0) {
        // Go to previous artist block, last trial
        setCurrentArtistIndex(prev => prev - 1);
        setCurrentBlockPhase('trials');
        setCurrentTrialIndex(15); // Assuming ~16 trials per block
        setReviewerNav({ blockPhase: 'trials', trialIndex: 15, postTaskIndex: null });
      } else if (currentBlockPhase === 'intro') {
        // Go from first artist intro to demographics
        changePhase('demographics');
        setCurrentBlockPhase('intro');
        setCurrentTrialIndex(0);
      }
    } else if (phase === 'postTask' && currentPostTaskIndex > 0) {
      // Navigate through post-task images
      const newIndex = currentPostTaskIndex - 1;
      setCurrentPostTaskIndex(newIndex);
      setReviewerNav({ blockPhase: null, trialIndex: null, postTaskIndex: newIndex });
    } else {
      // Navigate between main phases
      const phaseOrder = ['consent', 'instructions', 'demographics', 'experiment', 'postTask', 'export'];
      const currentIndex = phaseOrder.indexOf(phase);
      if (currentIndex > 0) {
        changePhase(phaseOrder[currentIndex - 1]);
        if (phaseOrder[currentIndex - 1] === 'experiment') {
          setCurrentBlockPhase('intro');
          setCurrentTrialIndex(0);
        }
      }
    }
  };

  const navigateNext = () => {
    // Enable navigation mode so child components receive reviewerNav updates
    setNavigationMode(true);
    
    if (phase === 'experiment') {
      if (currentBlockPhase === 'intro') {
        // Go from intro to familiarization
        setCurrentBlockPhase('familiarization');
        setReviewerNav({ blockPhase: 'familiarization', trialIndex: null, postTaskIndex: null });
      } else if (currentBlockPhase === 'familiarization') {
        // Go from familiarization to trials
        setCurrentBlockPhase('trials');
        setCurrentTrialIndex(0);
        setReviewerNav({ blockPhase: 'trials', trialIndex: 0, postTaskIndex: null });
      } else if (currentBlockPhase === 'trials') {
        if (currentTrialIndex < 15) { // Assuming ~16 trials per block
          // Go to next trial
          const newIndex = currentTrialIndex + 1;
          setCurrentTrialIndex(newIndex);
          setReviewerNav({ blockPhase: 'trials', trialIndex: newIndex, postTaskIndex: null });
        } else if (currentArtistIndex < artistOrder.length - 1) {
          // Go to next artist block
          setCurrentArtistIndex(prev => prev + 1);
          setCurrentBlockPhase('intro');
          setCurrentTrialIndex(0);
          setReviewerNav({ blockPhase: 'intro', trialIndex: null, postTaskIndex: null });
        } else {
          // Go to post-task
          changePhase('postTask');
          setCurrentPostTaskIndex(0);
          setReviewerNav({ blockPhase: null, trialIndex: null, postTaskIndex: 0 });
        }
      }
    } else if (phase === 'postTask') {
      if (currentPostTaskIndex < 9) { // Assuming 10 post-task images
        // Go to next post-task image
        const newIndex = currentPostTaskIndex + 1;
        setCurrentPostTaskIndex(newIndex);
        setReviewerNav({ blockPhase: null, trialIndex: null, postTaskIndex: newIndex });
      } else {
        // Go to export
        changePhase('export');
      }
    } else {
      // Navigate between main phases
      const phaseOrder = ['consent', 'instructions', 'demographics', 'experiment', 'postTask', 'export'];
      const currentIndex = phaseOrder.indexOf(phase);
      if (currentIndex < phaseOrder.length - 1 && currentIndex !== -1) {
        changePhase(phaseOrder[currentIndex + 1]);
        if (phaseOrder[currentIndex + 1] === 'experiment') {
          setCurrentBlockPhase('intro');
          setCurrentTrialIndex(0);
          setCurrentPostTaskIndex(0);
        } else if (phaseOrder[currentIndex + 1] === 'postTask') {
          setCurrentPostTaskIndex(0);
        }
      }
    }
  };


  return (
    <div className="experiment-container">
      {/* Reviewer Navigation Bar */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <button 
          onClick={navigatePrev}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            background: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            color: '#495057',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          ← Prev
        </button>
        
        <select
          value={getCurrentDropdownValue()}
          onChange={(e) => navigateToSection(e.target.value)}
          style={{
            padding: '6px 10px',
            fontSize: '13px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            background: 'white',
            color: '#495057',
            cursor: 'pointer',
            minWidth: '300px',
          }}
        >
          <optgroup label="Setup">
            <option value="consent">Consent Form</option>
            <option value="instructions">Instructions</option>
            <option value="demographics">Demographics</option>
          </optgroup>
          
          {artistOrder.length > 0 && (
            <optgroup label="Artist Blocks">
              {artistOrder.map((artist, idx) => (
                <option key={artist.id} value={`block_${idx + 1}_intro`}>
                  Block {idx + 1}: {artist.name}
                </option>
              ))}
            </optgroup>
          )}
          
          <optgroup label="Post-Task">
            <option value="postTask_1_recognition">Recognition</option>
            <option value="postTask_1_attribution">Attribution</option>
          </optgroup>
          
          <optgroup label="Export">
            <option value="export">Data Export</option>
          </optgroup>
        </select>
        
        <button 
          onClick={navigateNext}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            background: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            color: '#495057',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          Next →
        </button>
        
        <div style={{ width: '1px', height: '20px', background: '#dee2e6', margin: '0 4px' }} />
        
        <button 
          onClick={() => setShowSimulator(true)}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            background: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            color: '#495057',
            cursor: 'pointer',
            fontWeight: '500',
          }}
          title="Experiment Parameters"
        >
          Parameters
        </button>
      </div>
      {phase === 'consent' && (
        <Consent onComplete={handleConsentComplete} />
      )}
      
      {phase === 'instructions' && (
        <Instructions onComplete={handleInstructionsComplete} />
      )}
      
      {phase === 'demographics' && (
        <Demographics onComplete={handleDemographicsComplete} />
      )}
      
      {phase === 'experiment' && artistOrder.length > 0 && (
        <ArtistBlock
          artist={artistOrder[currentArtistIndex]}
          blockNumber={currentArtistIndex + 1}
          totalBlocks={artistOrder.length}
          participantID={participantData.participantID}
          onComplete={handleBlockComplete}
          reviewerNav={navigationMode ? reviewerNav : { blockPhase: null, trialIndex: null, postTaskIndex: null }}
          isFirstBlock={currentArtistIndex === 0}
          onPhaseChange={(newPhase) => setCurrentBlockPhase(newPhase)}
          onTrialIndexChange={(newIndex) => setCurrentTrialIndex(newIndex)}
        />
      )}
      
      {phase === 'postTask' && (
        <PostTaskCheck
          seenImages={participantData.blocks}
          onComplete={handlePostTaskComplete}
          reviewerNav={navigationMode ? reviewerNav : { blockPhase: null, trialIndex: null, postTaskIndex: null }}
        />
      )}
      
      {phase === 'export' && (
        <DataExport data={participantData} />
      )}
      
      {showSimulator && (
        <ExperimentSimulator onClose={() => setShowSimulator(false)} />
      )}
    </div>
  );
}

export default App;